/**
 * Queries and mutations for IT Admin → Email intake (option B, 19 Oct).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actOnEmailIntake as sdkAct,
  type EmailIntakeActionRequest,
  type EmailIntakeDetail,
  type EmailIntakeItem,
  type EmailIntakeListResponse,
  type EmailIntakeProcessed,
  type EmailIntakeSampleRequest,
  getEmailIntakeItem as sdkGetItem,
  listEmailIntake as sdkList,
  runEmailIntakeSamples as sdkSamples,
  uploadEmailIntakeMail as sdkUpload,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export type EmailIntakeFilter = "open_review" | "all";

export const emailIntakeKeys = {
  all: ["email-intake"] as const,
  list: (filter: EmailIntakeFilter, q: string) =>
    [...emailIntakeKeys.all, "list", filter, q] as const,
  item: (id: number) => [...emailIntakeKeys.all, "item", id] as const,
};

export function useEmailIntake(filter: EmailIntakeFilter, q: string) {
  return useQuery({
    queryKey: emailIntakeKeys.list(filter, q),
    queryFn: async (): Promise<EmailIntakeListResponse> => {
      const { data } = await sdkList({
        query: {
          status: filter === "all" ? undefined : filter,
          q: q.trim() === "" ? undefined : q.trim(),
          limit: 100,
        },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    refetchInterval: 60_000,
  });
}

export function useEmailIntakeItem(id: number | null) {
  return useQuery({
    queryKey: emailIntakeKeys.item(id ?? 0),
    queryFn: async (): Promise<EmailIntakeDetail> => {
      const { data } = await sdkGetItem({ path: { row_id: id ?? 0 }, throwOnError: true });
      return data;
    },
    enabled: id !== null,
    retry: false,
  });
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: emailIntakeKeys.all });
}

export function useEmailIntakeAction() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: EmailIntakeActionRequest;
    }): Promise<EmailIntakeItem> => {
      logger.debug("[email-intake] action", id, body.action);
      const { data } = await sdkAct({ path: { row_id: id }, body, throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useRunSamples() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (body: EmailIntakeSampleRequest): Promise<EmailIntakeProcessed> => {
      logger.debug("[email-intake] samples", body.names.length, body.mode ?? "default");
      const { data } = await sdkSamples({ body, throwOnError: true });
      return data;
    },
    onSuccess: refresh,
  });
}

export function useUploadMail() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({
      file,
      mode,
    }: {
      file: File;
      mode: "shadow" | "act" | null;
    }): Promise<EmailIntakeProcessed> => {
      logger.debug("[email-intake] upload", file.size, mode ?? "default");
      const { data } = await sdkUpload({
        body: mode === null ? { file } : { file, mode },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: refresh,
  });
}
